/**
 * Pure transforms turning the Owl diagram envelopes (shared `labels` + per-series
 * data) into the row/series shape `<Chart>` wants. The data is identical across
 * chart modes — Summary vs Divide only changes how it's drawn (line vs filled
 * area), handled by the page via the chart `type`, not here.
 */
import type { ChartSeries } from '@vnetwork/ui/charts';
import type {
  WafDnsQueryDiagram,
  WafEdgeQueryDiagram,
  WafPulseDiagram,
  WafMultiCdnDiagram,
  WafMultiCdnMetric,
  WafOriginShieldDiagram,
  WafThreatTopDiagram,
  WafThreatMetric,
} from '@/api/waf';

/** Summary = line (no fill); Divide = area (filled). Same series either way. */
export type ChartMode = 'summary' | 'divide';

const DAY = 86400;

/**
 * Bucket size (seconds) for a range, mirroring the vendor's auto-interval table
 * keyed by the total span — NOT a fixed bucket count. So a ≤1-day window (incl.
 * "Last 60 minutes") buckets at 3600s, ≤3d at 7200s, etc.
 */
export function intervalForRange(startSec: number, endSec: number): number {
  const span = Math.max(0, endSec - startSec);
  if (span <= 1 * DAY) return 3600;
  if (span <= 3 * DAY) return 7200;
  if (span <= 7 * DAY) return 14400;
  if (span <= 14 * DAY) return 43200;
  if (span <= 30 * DAY) return 86400;
  if (span <= 90 * DAY) return 259200;
  if (span <= 180 * DAY) return 604800;
  return 1209600;
}

/**
 * Bucket size (seconds) for a Pulse range — the vendor's `pulseReport` table is
 * finer/different from the generic analytics one above. Confirmed against the
 * vendor: ≤3d → 3600 (1h), ≤7d → 7200 (2h), longer → 86400 (1d). (≤1d and very
 * long ranges have no separate vendor tier observed, so they fold into these.)
 */
export function pulseIntervalForRange(startSec: number, endSec: number): number {
  const span = Math.max(0, endSec - startSec);
  if (span <= 3 * DAY) return 3600;
  if (span <= 7 * DAY) return 7200;
  return 86400;
}

/** Distinct chart colors (confirmed DS tokens), cycled when there are >1 series. */
const PALETTE = [
  'var(--vn-color-blue-400)',
  'var(--vn-color-green-400)',
  'var(--vn-color-amber-400)',
  'var(--vn-color-red-400)',
  'var(--vn-accent-solid)',
];
const SINGLE_COLOR = 'var(--vn-color-blue-400)';

/** Axis label for a bucket: date for daily buckets, date+hour for finer ones. */
export function fmtAxisLabel(ms: number, intervalSec: number): string {
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return String(ms);
  if (intervalSec >= 86400) {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit' });
}

/** A safe DOM/series key from an arbitrary label (Chart keys index row objects). */
const keyFor = (label: string, i: number) => `s${i}_${label.replace(/[^\w]/g, '_')}`;

export interface ChartData {
  rows: Record<string, unknown>[];
  series: ChartSeries[];
}

/** Build chart rows + series from the shared labels and a list of named lines. */
function build(
  labels: number[],
  lines: { label: string; values: number[] }[],
  intervalSec: number,
): ChartData {
  if (!labels.length || !lines.length) return { rows: [], series: [] };
  const single = lines.length === 1;
  const series: ChartSeries[] = lines.map((l, i) => ({
    key: keyFor(l.label, i),
    // Empty/absent backend labels must NOT fall through to the synthetic key
    // (`s0_…`) in the tooltip — blank them so only the value shows.
    label: l.label || '',
    color: single ? SINGLE_COLOR : PALETTE[i % PALETTE.length],
  }));
  const rows = labels.map((ms, i) => {
    const row: Record<string, unknown> = { t: fmtAxisLabel(ms, intervalSec) };
    lines.forEach((l, li) => {
      row[series[li].key] = Number(l.values[i]) || 0;
    });
    return row;
  });
  return { rows, series };
}

/** DNS query diagram → chart rows + series. */
export function dnsRows(
  diagram: WafDnsQueryDiagram | undefined,
  intervalSec: number,
): ChartData {
  const labels = diagram?.labels ?? [];
  const lines = (diagram?.datasets ?? []).map((ds) => ({ label: ds.label, values: ds.data }));
  return build(labels, lines, intervalSec);
}

/** Edge query diagram → chart rows + series (metric defaults to edge_requests). */
export function edgeRows(
  diagram: WafEdgeQueryDiagram | undefined,
  intervalSec: number,
  labelFor: (platformId: string) => string = (id) => id,
  metric = 'edge_requests',
): ChartData {
  const labels = diagram?.labels ?? [];
  const lines = (diagram?.datasets ?? []).map((ds) => {
    const values = ds.series?.[metric] ?? ds.series?.[Object.keys(ds.series ?? {})[0]] ?? [];
    return { label: labelFor(ds.label), values: values.map((v) => Number(v) || 0) };
  });
  return build(labels, lines, intervalSec);
}

/** Pulse geolocation diagram → chart rows + series (one line per platform/geo, picking `metric`). */
export function pulseRows(
  diagram: WafPulseDiagram | undefined,
  intervalSec: number,
  labelFor: (label: string) => string = (l) => l,
  metric = 'latency',
): ChartData {
  const labels = diagram?.labels ?? [];
  const lines = (diagram?.datasets ?? []).map((ds) => {
    const values = ds.series?.[metric] ?? ds.series?.[Object.keys(ds.series ?? {})[0]] ?? [];
    return { label: labelFor(ds.label), values: values.map((v) => Number(v) || 0) };
  });
  return build(labels, lines, intervalSec);
}

/** Origin Shield diagram (flat single series) → chart rows + series. */
export function originShieldRows(
  diagram: WafOriginShieldDiagram | undefined,
  intervalSec: number,
  label: string,
): ChartData {
  const labels = diagram?.labels ?? [];
  return build(labels, [{ label, values: diagram?.data ?? [] }], intervalSec);
}

const lineTotal = (xs: number[]) => xs.reduce((a, b) => a + b, 0);

/**
 * Multi-CDN diagram → chart rows + series (one line per CDN platform / country).
 * `metric` (the Traffic / Request tab) picks the normalized series per dataset.
 * Series with no positive value are dropped (legend + tooltip only list entities
 * with data); beyond `topN`, the long tail (e.g. ~150 countries) is folded into a
 * single "Others" band so the legend stays readable and the stack still sums to
 * the total. Skipped for rate metrics, where summing percentages is meaningless.
 */
export function multiCdnRows(
  diagram: WafMultiCdnDiagram | undefined,
  intervalSec: number,
  labelFor: (label: string) => string = (l) => l,
  metric: WafMultiCdnMetric = 'request',
  topN = 10,
): ChartData {
  const labels = diagram?.labels ?? [];
  let lines = (diagram?.datasets ?? [])
    .map((ds) => ({
      label: labelFor(ds.label),
      values: (ds[metric] ?? []).map((v) => Number(v) || 0),
    }))
    .filter((l) => l.values.some((v) => v > 0));

  if (!diagram?.isRate && lines.length > topN) {
    lines.sort((a, b) => lineTotal(b.values) - lineTotal(a.values));
    const top = lines.slice(0, topN);
    const rest = lines.slice(topN);
    const others = labels.map((_, i) => rest.reduce((acc, l) => acc + (l.values[i] || 0), 0));
    lines = [...top, { label: 'Others', values: others }];
  }

  return build(labels, lines, intervalSec);
}

/**
 * Multi-CDN diagram → entities ranked by the active metric (desc) for the table,
 * straight from the backend range `summary` (bytes/requests, or `%` hit-rate).
 */
export function multiCdnRanking(
  diagram: WafMultiCdnDiagram | undefined,
  metric: WafMultiCdnMetric,
): { label: string; total: number }[] {
  return (diagram?.summary ?? [])
    .map((s) => ({ label: s.label, total: Number(s[metric]) || 0 }))
    .sort((a, b) => b.total - a.total);
}

/** Per-series values of a threat top series for the active metric. */
const threatValues = (s: WafThreatTopDiagram['datasets'][number], metric: WafThreatMetric) =>
  (metric === 'traffic' ? s.total_traffic : s.total_request) ?? [];

/** Threat top-N diagram → chart rows + series (one line per ranked entity). */
export function threatTopRows(
  diagram: WafThreatTopDiagram | undefined,
  intervalSec: number,
  metric: WafThreatMetric,
  labelFor: (label: string) => string = (l) => l,
): ChartData {
  const labels = diagram?.labels ?? [];
  const lines = (diagram?.datasets ?? []).map((s) => ({
    label: labelFor(s.label),
    values: threatValues(s, metric),
  }));
  return build(labels, lines, intervalSec);
}

/** Threat top-N diagram → entities ranked by total metric (desc) for the table. */
export function threatTopRanking(
  diagram: WafThreatTopDiagram | undefined,
  metric: WafThreatMetric,
  labelFor: (label: string) => string = (l) => l,
): { label: string; total: number }[] {
  return (diagram?.datasets ?? [])
    .map((s) => ({
      label: labelFor(s.label),
      total: threatValues(s, metric).reduce((a, b) => a + (Number(b) || 0), 0),
    }))
    .sort((a, b) => b.total - a.total);
}
